import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useAppSelector } from 'src/hooks/hooks';
import { cancelLeave } from 'src/services/leaveService';
import { getEmployeeById } from 'src/services/employeeService';
import { AppActions } from 'src/store/AppReducer';
import {
  CANCELLATION_AUTO_CANCELLED_MESSAGE,
  CANCELLATION_IN_REVIEW_MESSAGE,
  CANCELLATION_SUBMITTED_FOR_REVIEW_MESSAGE,
  CANCELLATION_WINDOW_CLOSED_MESSAGE,
  doesCancellationRequireApproval,
  isCancellationInReview,
  isWithinCancellationWindow
} from './utils/cancellationUtils';

const CancelLeaveModal = ({
  open,
  toggle,
  data,
  employeeId,
  setSnackBarOpen,
  setSnackbarSeverity,
  setSnackBarMessage,
  getData,
  onCancelSuccess
}) => {
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const dispatch = useDispatch();

  const [leaveCancelReason, setLeaveCancelReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [employeeType, setEmployeeType] = useState('');

  useEffect(() => {
    if (open) {
      setLeaveCancelReason('');
      setReasonError('');
      getEmployeeById(employeeId)
        .then((res) => {
          setEmployeeType(res.data?.employeeFinance?.type.toUpperCase());
        })
        .catch(() => {});
    }
  }, [open, employeeId]);

  const getStatusLabel = (status, hrApproved) => {
    if (
      status === 'APPROVED' ||
      status === 'HR_APPROVED' ||
      (status === 'RM_APPROVED' && !hrApproved)
    ) {
      return 'Approved';
    }
    if (status === 'RM_APPROVED' && hrApproved) {
      return 'Approved by PM/RM';
    }
    if (status === 'HR_REJECTED') {
      return 'Rejected by HR';
    }
    if (status === 'RM_REJECTED') {
      return 'Rejected';
    }
    if (status === 'PENDING') {
      return 'Pending';
    }
    if (status === 'CANCELLATION_REQUESTED') {
      return 'Cancellation Requested';
    }
    if (status === 'CANCELLATION_REJECTED') {
      return 'Cancellation Rejected';
    }
    if (status === 'CANCELLED') {
      return 'Cancelled';
    }
    return status || '-';
  };

  const getStatusColor = (status) => {
    if (
      status === 'APPROVED' ||
      status === 'RM_APPROVED' ||
      status === 'HR_APPROVED'
    ) {
      return '#00d688';
    }
    if (status === 'HR_REJECTED' || status === 'RM_REJECTED') {
      return '#FF1943';
    }
    if (status === 'PENDING') {
      return '#FFA319';
    }
    if (status === 'CANCELLATION_REQUESTED') {
      return '#FF1943';
    }
    if (status === 'CANCELLATION_REJECTED') {
      return '#E65100';
    }
    if (status === 'CANCELLED') {
      return '#3376c9';
    }
    return '#223354';
  };

  const formatLeaveDate = () => {
    const leaveDate = data?.fromDate || data?.toDate;
    if (!leaveDate) {
      return '-';
    }
    const formatted = moment(leaveDate).format('DD MMM YYYY');
    if (
      data?.totalDays > 1 &&
      data?.dayIndex !== undefined &&
      data?.dayIndex !== null
    ) {
      return `${formatted} (Day ${data.dayIndex + 1} of ${data.totalDays})`;
    }
    return formatted;
  };

  const leaveTypeLabel = data?.isEligible
    ? 'Lieu Leave Eligibility'
    : data?.typeName || '-';

  const dayTypeLabel =
    data?.typeName === 'Short Leave'
      ? 'N/A'
      : typeof data?.dateType === 'boolean'
      ? data.dateType
        ? 'Full Day'
        : 'Half Day'
      : '-';

  const isFullOrHalfDayLeave =
    data?.typeName !== 'Short Leave' && typeof data?.dateType === 'boolean';

  const handleClose = () => {
    setLeaveCancelReason('');
    setReasonError('');
    toggle();
  };

  const handleConfirm = () => {
    if (isCancellationInReview(data)) {
      setSnackBarMessage(CANCELLATION_IN_REVIEW_MESSAGE);
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
      handleClose();
      return;
    }
    if (!isWithinCancellationWindow(data)) {
      setSnackBarMessage(CANCELLATION_WINDOW_CLOSED_MESSAGE);
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
      handleClose();
      return;
    }
    if (!isFullOrHalfDayLeave) {
      setReasonError(
        'Only Full Day or Half Day leaves can be cancelled completely. Shorter/longer leaves are not allowed to be removed.'
      );
      return;
    }
    if (!leaveCancelReason.trim()) {
      setReasonError('Please enter a valid reason.');
      return;
    }
    if (leaveCancelReason.length > 500) {
      setReasonError('Reason cannot exceed 500 characters');
      return;
    }

    dispatch(AppActions.showLoader());
    const cancelLeaveData = {
      id: data.id,
      employeeId: employeeId,
      cancelReason: leaveCancelReason.trim()
    };

    cancelLeave(cancelLeaveData.id, employeeType, cancelLeaveData)
      .then((res) => {
        const status =
          res?.data?.data?.status ||
          res?.data?.status ||
          '';
        const autoCancelled =
          String(status).toUpperCase() === 'CANCELLED' ||
          !doesCancellationRequireApproval(data);
        setSnackBarMessage(
          autoCancelled
            ? CANCELLATION_AUTO_CANCELLED_MESSAGE
            : CANCELLATION_SUBMITTED_FOR_REVIEW_MESSAGE
        );
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        handleClose();
        if (typeof onCancelSuccess === 'function') {
          dispatch(AppActions.hideLoader());
          onCancelSuccess(data.id);
        } else {
          getData(employeeId);
          dispatch(AppActions.hideLoader());
        }
      })
      .catch((error) => {
        const apiMessage = error?.response?.data?.message;
        setSnackBarMessage(
          isCancellationInReview(data)
            ? CANCELLATION_IN_REVIEW_MESSAGE
            : apiMessage || 'Unable to cancel a leave'
        );
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
        handleClose();
        dispatch(AppActions.hideLoader());
      });
  };

  const summaryRow = (label, value, valueColor?: string) => (
    <Grid
      container
      sx={{
        py: 0.75,
        alignItems: 'center'
      }}
    >
      <Grid item xs={4}>
        <Typography
          sx={{
            fontSize: '13px',
            color: '#6B7280',
            fontFamily:
              'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif'
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8} sx={{ textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: valueColor ? 600 : 500,
            color: valueColor || '#223354',
            fontFamily:
              'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif'
          }}
        >
          {value}
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '10px',
          width: '28rem',
          maxWidth: '92vw'
        }
      }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 2.5 }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '18px',
              color: '#223354',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif'
            }}
          >
            Cancel Leave
          </Typography>
          <IconButton
            id="btn-cancel-leave-close"
            size="small"
            onClick={handleClose}
            sx={{ color: '#6B7280' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Grid>

        <Box
          sx={{
            background: '#F5F7FF',
            borderRadius: '8px',
            px: 2,
            py: 1.25,
            mb: 2
          }}
        >
          {summaryRow('Leave type', leaveTypeLabel)}
          {summaryRow('Full Day/Half Day', dayTypeLabel)}
          {summaryRow('Date', formatLeaveDate())}
          {summaryRow(
            'Status',
            getStatusLabel(data?.status, data?.hrApproved),
            getStatusColor(data?.status)
          )}
        </Box>

        <TextField
          id="cancel-leave-reason"
          fullWidth
          multiline
          minRows={4}
          maxRows={6}
          placeholder="Enter reason to cancel"
          value={leaveCancelReason}
          error={Boolean(reasonError)}
          helperText={reasonError}
          inputProps={{ maxLength: 500 }}
          onChange={(e) => {
            setLeaveCancelReason(e.target.value);
            if (reasonError) {
              setReasonError('');
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              borderRadius: '8px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
              '& fieldset': {
                borderColor: reasonError ? undefined : '#E5E7EB'
              }
            },
            '& .MuiFormHelperText-root': {
              marginLeft: 0
            }
          }}
        />
        <Typography
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            fontSize: '12px',
            color: '#9CA3AF',
            mt: 0.5,
            mb: 2.5
          }}
        >
          {leaveCancelReason.length}/500
        </Typography>

        <Grid container justifyContent="flex-end" spacing={1.5}>
          <Grid item>
            <Button
              id="btn-cancel-leave-cancel"
              variant="outlined"
              disabled={isLoading}
              onClick={handleClose}
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                color: '#5569ff',
                background: '#F5F7FF',
                borderColor: '#F5F7FF',
                px: 2.5,
                '&:hover': {
                  background: '#EEF0FF',
                  borderColor: '#EEF0FF'
                }
              }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              id="btn-cancel-leave-confirm"
              variant="contained"
              disabled={isLoading}
              onClick={handleConfirm}
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                background: '#0069E4',
                px: 2.5,
                boxShadow: '0 2px 6px rgba(0, 105, 228, 0.35)',
                '&:hover': {
                  background: '#0056c1'
                }
              }}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default CancelLeaveModal;
